import { useState } from 'react';

interface Project {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  features: string[];
  color: string;
}

export default function Portfolio() {
  const [activeProject, setActiveProject] = useState<string>('m7');

  const projects: Project[] = [
    {
      id: 'm7',
      name: 'Banda M7',
      tagline: 'Energia que contagia',
      description: 'A Banda M7 transforma seu evento em uma celebração única. Combina uma energia contagiante e um repertório envolvente e atual, o que faz os seu convidados cantar e dançar com a M7. Performance de palco impecável, garantindo uma atmosfera perfeita e memorável!',
      image: 'https://static.readdy.ai/image/f3ee8701b883937bc520e8fddc6bcc4d/2a5aa9629cb965c8ee64fbc4af5366cc.jpeg',
      features: ['Repertório atual e clássico', 'Formação completa', 'Interação com público', 'Produção premium'],
      color: 'from-[#F07902] to-[#1E5BDA]'
    },
    {
      id: 'mozart',
      name: 'Coral Mozart',
      tagline: 'Sofisticação e emoção',
      description: 'Formações flexíveis de 3 a 40 músicos para casamentos e cerimônias sofisticadas. Arranjos refinados e impacto emocional garantido. Cada nota é pensada para criar a atmosfera perfeita do seu momento especial.',
      image: 'https://static.readdy.ai/image/f3ee8701b883937bc520e8fddc6bcc4d/d7c9e29da3c9bf51ccb938bec319e2af.jpeg',
      features: ['3 a 40 músicos', 'Arranjos sofisticados', 'Social e Corporativo', 'Repertório clássico e contemporâneo'],
      color: 'from-[#F07902] to-[#E3B650]'
    },
    {
      id: 'dixie',
      name: 'Dixie',
      tagline: 'Música Itinerante e Interativa',
      description: 'A Dixie Feel Music é uma formação móvel, leve e cheia de personalidade, ideal para shoppings, eventos corporativos e ações promocionais. Os músicos circulam pelo ambiente tocando clássicos do jazz, pop e temas festivos em versões divertidas, criando conexão instantânea com o público. Uma experiência musical viva, próxima e inesquecível.',
      image: 'https://static.readdy.ai/image/f3ee8701b883937bc520e8fddc6bcc4d/11b70e41f670dede1bf2e279612eee25.jpeg',
      features: ['Trio itinerante (Sax + Sousafone + Caixa)', 'Quarteto com Banjo ou Trombone', 'Repertório temático personalizável', 'Ideal para datas como Natal, Páscoa, Dia das Mães'],
      color: 'from-[#F07902] to-[#FF7AB8]'
    },
    {
      id: 'happyhour',
      name: 'Happy Hour',
      tagline: 'Programação Musical para Shoppings',
      description: 'A Feel Music desenvolve programação musical exclusiva para shoppings, criando ambientes mais acolhedores, aumentando o tempo de permanência do público e fortalecendo a experiência de compra. Nossa curadoria seleciona músicos ao vivo, repertórios e formatos ideais para cada momento, de apresentações intimistas a performances temáticas e datas especiais.',
      image: 'https://static.readdy.ai/image/f3ee8701b883937bc520e8fddc6bcc4d/7f2107a887fa33b8b5101bd3a3a6ef0a.jpeg',
      features: ['Curadoria musical contínua', 'Aumento de permanência e engajamento', 'Experiência sensorial e ambiente valorizado', 'Datas temáticas e campanias promocionais', 'Gerenciamento completo da programação artística'],
      color: 'from-[#F07902] to-[#B08AFF]'
    },
    {
      id: 'villa',
      name: 'Villa Voice',
      tagline: 'Potência vocal que emociona',
      description: 'Coral show com 12 vozes e banda completa, inspirado nos grandes corais americanos. Potência vocal, emoção e espetáculo em cada apresentação. Performances que arrepiam e criam memórias eternas.',
      image: 'https://static.readdy.ai/image/f3ee8701b883937bc520e8fddc6bcc4d/8b36bfea897c856e6febc2ab5026243c.jpeg',
      features: ['12 vozes profissionais', 'Banda completa', 'Estilo coral americano', 'Shows emocionantes'],
      color: 'from-[#F07902] to-[#E53038]'
    }
  ];

  const currentProject = projects.find(p => p.id === activeProject) || projects[0];

  const getCtaText = (projectId: string) => {
    if (projectId === 'happyhour') return 'Solicitar proposta para Shopping';
    if (projectId === 'dixie') return 'Solicitar Agenda da Dixie';
    return 'Solicitar Proposta';
  };

  return (
    <section id="portfolio" className="py-24 px-6 bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-amber-500 font-semibold text-sm uppercase tracking-wider">Portfólio Artístico</span>
          <div className="w-20 h-1 bg-amber-500 mx-auto mt-2 mb-6"></div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nossos Projetos Musicais
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Cinco formações artísticas únicas para criar a trilha sonora perfeita do seu evento
          </p>
        </div>

        {/* Project Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {projects.map((project) => (
            <button
              key={project.id}
              onClick={() => setActiveProject(project.id)}
              className={`px-6 py-3 rounded-full font-bold text-base transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeProject === project.id
                  ? `bg-gradient-to-r ${project.color} text-white shadow-lg scale-105`
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              {project.name}
            </button>
          ))}
        </div>

        {/* Project Content */}
        <div className="bg-black rounded-3xl overflow-hidden shadow-2xl">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative h-96 md:h-auto overflow-hidden" data-product-shop>
              <img
                src={currentProject.image}
                alt={currentProject.name}
                className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-110"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${currentProject.color} opacity-20`}></div>
            </div>

            {/* Content */}
            <div className="p-12 flex flex-col justify-center">
              <div className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${currentProject.color} text-white font-semibold text-sm mb-6 w-fit`}>
                {currentProject.tagline}
              </div>

              <h3 className="text-4xl font-bold text-white mb-6">
                {currentProject.name}
              </h3>

              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {currentProject.description}
              </p>

              {/* Features */}
              <div className="grid grid-cols-1 gap-4 mb-8">
                {currentProject.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-music-2-fill text-amber-500 text-xl"></i>
                    </div>
                    <span className="text-gray-400 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  contactSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`px-8 py-4 bg-gradient-to-r ${currentProject.color} text-white font-bold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer w-fit`}
              >
                <span className="flex items-center justify-center gap-2">
                  {getCtaText(currentProject.id)}
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
